import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { publicAPI, getMediaUrl, getMediaType } from '../services/api.jsx';
import { Link, useParams, useNavigate } from 'react-router-dom';

const Gallery = () => {
    const { t, currentLanguage } = useTranslation();
    const { folderId } = useParams();
    const navigate = useNavigate();

    const [folders, setFolders] = useState([]);
    const [images, setImages] = useState([]);
    const [uncategorizedImages, setUncategorizedImages] = useState([]);
    const [view, setView] = useState(folderId ? 'images' : 'folders');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
    const [currentImageList, setCurrentImageList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [imageErrors, setImageErrors] = useState({});
    const [currentFolder, setCurrentFolder] = useState(null);

    const itemsPerPage = 12;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const fetchFoldersAndUncategorized = useCallback(async () => {
        try {
            setLoading(true);
            const [foldersResponse, uncategorizedResponse] = await Promise.all([
                publicAPI.getPublicGalleryFolders(),
                publicAPI.getUncategorizedImages()
            ]);
            setFolders(foldersResponse);
            setUncategorizedImages(uncategorizedResponse);
            setView('folders');
        } catch (error) {
            console.error('Error fetching gallery data:', error);
            setError(t('gallery.folderFetchError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const fetchImagesInFolder = useCallback(async (folderId) => {
        try {
            setLoading(true);
            const [imagesResponse, foldersResponse] = await Promise.all([
                publicAPI.getImagesInFolder(folderId, currentPage, itemsPerPage),
                publicAPI.getPublicGalleryFolders()
            ]);
            const folder = foldersResponse.find(f => f.id === parseInt(folderId));
            setCurrentFolder(folder);
            setImages(imagesResponse);
            const countResponse = await publicAPI.getImagesInFolderCount(folderId);
            setTotalItems(countResponse.total);
            setView('images');
        } catch (error) {
            console.error(`Error fetching images for folder ${folderId}:`, error);
            setError(t('gallery.imageFetchError'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, t]);

    useEffect(() => {
        if (folderId) {
            fetchImagesInFolder(folderId);
        } else {
            fetchFoldersAndUncategorized();
        }
    }, [folderId, fetchFoldersAndUncategorized, fetchImagesInFolder, currentPage]);


    const handleMediaClick = (media, index, list) => {
        setSelectedMedia(media);
        setSelectedMediaIndex(index);
        setCurrentImageList(list);
    };

    const handleCloseModal = useCallback(() => {
        setSelectedMedia(null);
        setSelectedMediaIndex(0);
        setCurrentImageList([]);
    }, []);

    const handlePrevImage = useCallback(() => {
        if (selectedMediaIndex > 0) {
            const newIndex = selectedMediaIndex - 1;
            setSelectedMediaIndex(newIndex);
            setSelectedMedia(currentImageList[newIndex]);
        }
    }, [selectedMediaIndex, currentImageList]);

    const handleNextImage = useCallback(() => {
        if (selectedMediaIndex < currentImageList.length - 1) {
            const newIndex = selectedMediaIndex + 1;
            setSelectedMediaIndex(newIndex);
            setSelectedMedia(currentImageList[newIndex]);
        }
    }, [selectedMediaIndex, currentImageList]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Escape') {
            handleCloseModal();
        } else if (e.key === 'ArrowLeft') {
            handlePrevImage();
        } else if (e.key === 'ArrowRight') {
            handleNextImage();
        }
    }, [handleCloseModal, handlePrevImage, handleNextImage]);

    const handleImageError = (imageId) => {
        setImageErrors(prev => ({
            ...prev,
            [imageId]: true
        }));
    };

    const handleImageLoad = (imageId) => {
        setImageErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[imageId];
            return newErrors;
        });
    };

    const goToPage = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        if (selectedMedia) {
            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }
    }, [selectedMedia, selectedMediaIndex, handleKeyPress]);

    const getLocalizedDescription = (item) => {
        if (!item) return '';
        const byLang = {
            de: item.description_de,
            en: item.description_en,
            it: item.description_it,
        };
        const primary = byLang[currentLanguage];
        if (primary && primary.trim()) return primary.trim();
        const fallback =
            byLang.de?.trim() ||
            byLang.en?.trim() ||
            byLang.it?.trim() ||
            (item.description && item.description.trim()) ||
            '';
        return fallback;
    };

    const renderFolderThumbnail = (folder) => {
        const coverUrl = folder.cover_image_path ? getMediaUrl(folder.cover_image_path) : null;
        const description = getLocalizedDescription(folder);

        return (
            <Link
                to={`/gallery/folder/${folder.id}`}
                key={folder.id}
                aria-label={`${description || t('gallery.folder')} (${t('gallery.folder')})`}
                className="relative bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:ring-offset-2 touch-feedback active:scale-[0.98]"
            >
                <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden bg-neutral-100">
                    {coverUrl ? (
                        <img
                            src={coverUrl}
                            alt={description}
                            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105 group-focus:scale-105"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path></svg>
                        </div>
                    )}

                    {/* Dark gradient for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-90" />

                    {/* Subtle ring on hover/focus */}
                    <div className="pointer-events-none absolute inset-0 ring-0 group-hover:ring-4 group-focus:ring-4 ring-primary-400/10 transition-all duration-500" />

                    {/* Folder badge (top-left) */}
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex items-center space-x-2">
                        <div className="relative">
                            <div className="bg-neutral-900/70 backdrop-blur-sm text-white rounded-lg px-2 py-0.5 sm:px-2.5 sm:py-1 flex items-center space-x-1 sm:space-x-1.5 shadow-md shadow-black/30 border border-white/10">
                                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-primary-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2Z" />
                                </svg>
                                <span className="text-[10px] sm:text-xs font-medium tracking-wide uppercase text-white/90">{t('gallery.folder')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Title & subtle meta */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                        <h3 className="text-white text-base sm:text-lg font-semibold leading-snug line-clamp-2 drop-shadow-lg">
                            {description}
                        </h3>
                        {folder.image_count != null && (
                            <div className="mt-1.5 sm:mt-2 flex items-center space-x-2 text-[10px] sm:text-xs text-white/80 font-medium">
                                <div className="flex items-center space-x-1">
                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="3" />
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09c0 .7.41 1.34 1.04 1.63.23.1.47.2.7.33" />
                                    </svg>
                                    <span>{folder.image_count}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        );
    };

    const renderMediaThumbnail = (item, index) => {
        const mediaType = getMediaType(item);
        const mediaUrl = getMediaUrl(item.filename);

        if (mediaType === 'video') {
            return (
                <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden bg-black">
                    <video
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={() => handleImageError(item.id)}
                        onLoadedData={() => handleImageLoad(item.id)}
                        muted
                        preload="metadata"
                        playsInline
                        crossOrigin="anonymous"
                    >
                        <source src={`${mediaUrl}#t=0.1`} type={item.mime_type || 'video/mp4'} />
                        Your browser does not support the video tag.
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 group-hover:opacity-0">
                        <div className="bg-black bg-opacity-50 rounded-full p-2 sm:p-3">
                            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                    {!imageErrors[item.id] ? (
                        <img
                            src={mediaUrl}
                            alt={getLocalizedDescription(item) || item.original_filename}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={() => handleImageError(item.id)}
                            onLoad={() => handleImageLoad(item.id)}
                            loading="lazy"
                            crossOrigin="anonymous"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                            <div className="text-center p-4">
                                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-neutral-400 mx-auto mb-2 sm:mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p className="text-neutral-400 text-xs sm:text-sm">{t('gallery.imageNotAvailable')}</p>
                            </div>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                        </div>
                    </div>
                </div>
            );
        }
    };

    const renderModalMedia = (item) => {
        const mediaType = getMediaType(item);
        const mediaUrl = getMediaUrl(item.filename);

        if (mediaType === 'video') {
            return (
                <video
                    className="max-w-full max-h-[80vh] object-contain"
                    controls
                    autoPlay
                    muted
                    playsInline
                    crossOrigin="anonymous"
                    preload="metadata"
                >
                    <source src={mediaUrl} type={item.mime_type || 'video/mp4'} />
                    Your browser does not support the video tag.
                </video>
            );
        } else {
            return (
                <img
                    src={mediaUrl}
                    alt={getLocalizedDescription(item) || item.original_filename}
                    className="max-w-full max-h-[80vh] object-contain"
                    crossOrigin="anonymous"
                />
            );
        }
    };

    const renderGrid = (items, listType) => (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {items.map((item, index) => (
                <div
                    key={item.id}
                    className="bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer group touch-feedback active:scale-[0.98]"
                    onClick={() => handleMediaClick(item, index, items)}
                >
                    {renderMediaThumbnail(item, index)}
                    {getLocalizedDescription(item) && (
                        <div className="p-3 sm:p-4">
                            <p className="text-xs sm:text-sm text-neutral-600 line-clamp-2">
                                {getLocalizedDescription(item)}
                            </p>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = window.innerWidth < 640 ? 3 : 5;
        const halfVisible = Math.floor(maxVisiblePages / 2);

        let startPage = Math.max(1, currentPage - halfVisible);
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            pages.push(
                <button key={1} onClick={() => goToPage(1)} className="min-w-[36px] sm:min-w-[44px] h-9 sm:h-11 flex items-center justify-center rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 text-neutral-700 hover:text-primary-600 hover:bg-primary-50 active:scale-95 touch-target">
                    1
                </button>
            );
            if (startPage > 2) {
                pages.push(<span key="ellipsis1" className="px-1 sm:px-2 py-3 text-neutral-400 font-semibold text-xs sm:text-base">...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button key={i} onClick={() => goToPage(i)} className={`min-w-[36px] sm:min-w-[44px] h-9 sm:h-11 flex items-center justify-center rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 touch-target ${i === currentPage ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg shadow-primary-500/25 scale-105' : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50 active:scale-95'}`}>
                    {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis2" className="px-1 sm:px-2 py-3 text-neutral-400 font-semibold text-xs sm:text-base">...</span>);
            }
            pages.push(
                <button key={totalPages} onClick={() => goToPage(totalPages)} className="min-w-[36px] sm:min-w-[44px] h-9 sm:h-11 flex items-center justify-center rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 text-neutral-700 hover:text-primary-600 hover:bg-primary-50 active:scale-95 touch-target">
                    {totalPages}
                </button>
            );
        }

        return (
            <div className="mt-8 sm:mt-16 flex justify-center">
                <nav className="flex items-center space-x-0.5 sm:space-x-1 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-neutral-200 p-1.5 sm:p-2">
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 touch-target ${currentPage === 1 ? 'text-neutral-400 cursor-not-allowed bg-neutral-50' : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50 active:scale-95'}`}>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        <span className="hidden sm:inline">{t('common.back')}</span>
                    </button>
                    <div className="flex items-center space-x-0.5 sm:space-x-1">
                        {pages}
                    </div>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 touch-target ${currentPage === totalPages ? 'text-neutral-400 cursor-not-allowed bg-neutral-50' : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50 active:scale-95'}`}>
                        <span className="hidden sm:inline">{t('common.next')}</span>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 sm:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                    </button>
                </nav>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Header */}
            <div className="bg-white border-b border-neutral-150 pt-16 sm:pt-24">
                <div className="container-custom py-10 sm:py-16 md:py-20">
                    <div className="max-w-3xl">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-950 mb-4 sm:mb-6 leading-tight">
                            {view === 'folders' ? t('gallery.title') : (currentFolder ? getLocalizedDescription(currentFolder) : t('gallery.title'))}
                        </h1>
                        <div className="text-base sm:text-lg md:text-xl text-neutral-700 leading-relaxed">
                            {view === 'folders' && t('gallery.description').split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    {index < t('gallery.description').split('\n').length - 1 && <br />}
                                </React.Fragment>
                            ))}
                            {view === 'images' && (
                                <button onClick={() => navigate('/gallery')} className="text-primary-600 hover:underline touch-feedback flex items-center py-2">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                    {t('gallery.backToFolders')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container-custom py-6 sm:py-8 md:py-12">
                {loading ? (
                    <div className="flex justify-center items-center py-16 sm:py-24">
                        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-2 border-primary-600 border-t-transparent"></div>
                    </div>
                ) : error ? (
                    <div className="text-center py-16 sm:py-24">
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 sm:p-8 max-w-md mx-auto">
                            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-red-400 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <h3 className="text-lg sm:text-xl font-semibold text-red-900 mb-2">{t('gallery.error')}</h3>
                            <p className="text-sm sm:text-base text-red-700">{error}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {view === 'folders' && (
                            <>
                                {folders.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                                        {folders.map(renderFolderThumbnail)}
                                    </div>
                                )}

                                {uncategorizedImages.length > 0 && (
                                    <>
                                        {folders.length > 0 && (
                                            <h2 className="text-xl sm:text-2xl font-bold text-neutral-950 mt-10 sm:mt-16 mb-4 sm:mb-6">{t('gallery.uncategorizedImagesTitle')}</h2>
                                        )}
                                        {renderGrid(uncategorizedImages, 'uncategorized')}
                                    </>
                                )}

                                {folders.length === 0 && uncategorizedImages.length === 0 && (
                                    <div className="text-center py-16 sm:py-24">
                                        <div className="bg-white rounded-xl border border-neutral-200 p-10 sm:p-16 max-w-md mx-auto shadow-sm">
                                            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-400 mx-auto mb-4 sm:mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <h3 className="text-lg sm:text-xl font-semibold text-neutral-950 mb-2 sm:mb-3">{t('gallery.noImages')}</h3>
                                            <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">{t('gallery.noImagesDescription')}</p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {view === 'images' && (
                            <>
                                {images.length > 0 ? (
                                    renderGrid(images, 'folder')
                                ) : (
                                    <div className="text-center py-16 sm:py-24">
                                        <div className="bg-white rounded-xl border border-neutral-200 p-10 sm:p-16 max-w-md mx-auto shadow-sm">
                                            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-400 mx-auto mb-4 sm:mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <h3 className="text-lg sm:text-xl font-semibold text-neutral-950 mb-2 sm:mb-3">{t('gallery.noImages')}</h3>
                                            <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">{t('gallery.noImagesInFolderDescription')}</p>
                                        </div>
                                    </div>
                                )}
                                {renderPagination()}
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Media Modal - Mobile optimized */}
            {selectedMedia && (
                <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-2 sm:p-4 safe-area-top safe-area-bottom">
                    <div className="relative max-w-7xl max-h-full w-full">
                        {/* Close button - Larger for mobile */}
                        <button onClick={handleCloseModal} className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2.5 sm:p-2 rounded-full transition-colors z-10 touch-target">
                            <svg className="w-6 h-6 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Navigation buttons - Optimized for touch */}
                        {selectedMediaIndex > 0 && (
                            <button onClick={handlePrevImage} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2.5 sm:p-3 rounded-full transition-colors z-10 touch-target">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}

                        {selectedMediaIndex < currentImageList.length - 1 && (
                            <button onClick={handleNextImage} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2.5 sm:p-3 rounded-full transition-colors z-10 touch-target">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}

                        {/* Image counter on mobile */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs sm:text-sm px-3 py-1 rounded-full">
                            {selectedMediaIndex + 1} / {currentImageList.length}
                        </div>

                        {renderModalMedia(selectedMedia)}
                    </div>
                </div>
            )}
        </div>
    );
};
export default Gallery;